import { useState, useEffect, useCallback, useRef } from 'react';
import { getApiUrl } from '../../../../config/api';
import { getStableAccumulatedDataExtraQueryParams } from '../../../../services/dataService';
import { fetchStableAccumulatedJson } from '../../../../services/stableAccumulatedRequest';
import {
  loadReferenceSymbolFiltersState,
  referenceFiltersToQueryRecord,
} from '../../../../utils/referenceSymbolFiltersShared';
import { inferFlowTradePosition } from '../../../../utils/flowVolumeProfile';
import type {
  FlowTrade,
  GEXProfileMode,
  LadderBar,
  OptionsProfileLookbackByStrike,
  OptionsProfileStrike,
  OrderflowSeriesPoint,
  SkewPoint,
  StateLadders,
} from '../types';

const REFRESH_MS = 15_000;
const REQUEST_COOLDOWN_MS = 8_000;

type ProfileSnapshot = { ts: number; byStrike: Map<number, { callNet: number; putNet: number }> };

export interface FlowDataFetchOptions {
  /** QuestDB + RAM: hours of flow history (default 24 for symbolScoped). */
  historyHours?: number;
  limit?: number;
  minPremium?: number;
  /** Pass ticker + from_ts so backend queries QuestDB by symbol (not global head slice). */
  symbolScoped?: boolean;
}

export function useFlowData(
  symbol: string,
  profileMode: GEXProfileMode = 'latest',
  fetchOptions?: FlowDataFetchOptions,
) {
  const [flowTrades, setFlowTrades] = useState<FlowTrade[]>([]);
  const [optionsProfile, setOptionsProfile] = useState<OptionsProfileStrike[]>([]);
  const [profileLookbackByStrike, setProfileLookbackByStrike] = useState<OptionsProfileLookbackByStrike>({});
  const [skewPoints, setSkewPoints] = useState<SkewPoint[]>([]);
  const [stateLadders, setStateLadders] = useState<StateLadders>({ dex: [], convexity: [], vanna: [], charm: [] });
  const [orderflowSeries, setOrderflowSeries] = useState<OrderflowSeriesPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [flowMeta, setFlowMeta] = useState<{ total: number; source?: string }>({
    total: 0,
  });
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const historyRef = useRef<ProfileSnapshot[]>([]);
  const lastRequestByKeyRef = useRef<Map<string, number>>(new Map());
  const inFlightKeysRef = useRef<Set<string>>(new Set());

  const fetch_ = useCallback(async () => {
    if (!symbol) return;
    const key = `${symbol.toUpperCase()}|${profileMode}`;
    if (inFlightKeysRef.current.has(key)) return;
    const now = Date.now();
    const lastRequest = lastRequestByKeyRef.current.get(key) || 0;
    if (now - lastRequest < REQUEST_COOLDOWN_MS) return;
    lastRequestByKeyRef.current.set(key, now);
    inFlightKeysRef.current.add(key);
    setLoading(true);
    try {
      // Primary: live/stored flow endpoint
      const scoped = Boolean(fetchOptions?.symbolScoped);
      const historyHours = fetchOptions?.historyHours ?? (scoped ? 24 : 0);
      const rowLimit = fetchOptions?.limit ?? (scoped ? 12_000 : 1500);
      const minPrem = fetchOptions?.minPremium ?? 1000;

      const flowParams = new URLSearchParams();
      flowParams.set('ticker', symbol);
      flowParams.set('underlying', symbol);
      flowParams.set('limit', String(rowLimit));
      flowParams.set('min_premium', String(minPrem));
      if (scoped && historyHours > 0) {
        const from = new Date(Date.now() - historyHours * 60 * 60 * 1000);
        flowParams.set('from_ts', from.toISOString().slice(0, 19));
        flowParams.set('to_ts', new Date().toISOString().slice(0, 19));
      }
      Object.entries(referenceFiltersToQueryRecord(loadReferenceSymbolFiltersState())).forEach(([k, v]) => {
        if (v) flowParams.set(k, v);
      });
      const primaryUrl = getApiUrl(`/api/options-flow/flow?${flowParams.toString()}`);
      const primaryRes = await fetch(primaryUrl);
      const primaryJson = await primaryRes.json();
      let rawRows: any[] = primaryJson.data || [];
      setFlowMeta({
        total: Number(primaryJson.total) || rawRows.length,
        source: typeof primaryJson.source === 'string' ? primaryJson.source : undefined,
      });

      // Fallback: if flow is sparse, derive trade-like rows from stable accumulated data
      if (!rawRows.length) {
        const stableUrl = getApiUrl(
          `/api/accumulated-data?source=stable&underlyings=${encodeURIComponent(symbol)}&${getStableAccumulatedDataExtraQueryParams()}`
        );
        const stableJson = await fetchStableAccumulatedJson(stableUrl);
        const stableRows: any[] = stableJson.data || [];
        rawRows = stableRows.slice(0, 2000).map((r) => {
          const side = String(r['Option Type'] || r.option_type || 'C').toUpperCase();
          const bidVol = Number(r['Bid Volume'] ?? r.bid_volume ?? 0);
          const askVol = Number(r['Ask Volume'] ?? r.ask_volume ?? 0);
          const tradeSide = askVol >= bidVol ? 'ask' : 'bid';
          return {
            Time: new Date().toISOString(),
            Ticker: r.Underlying || symbol,
            Strike: Number(r.Strike || 0),
            Side: side === 'C' ? 'CALL' : 'PUT',
            OptionType: side,
            'Option Type': side,
            Volume: Number(r.Volume || 0),
            Premium: Number(r.Premium || 0),
            IV: Number(r.IV || r['Implied Volatility'] || 0),
            'Expiry Date': String(r['Expiry Date'] || r.expiry || ''),
            Bid_Ask: tradeSide,
            bid_ask: tradeSide,
            Price: Number(r.Last || r.price || 0),
            Spot: Number(r['Underlying Price'] || 0),
          };
        });
      }

      const rows = applyFlowProfileMode(rawRows, profileMode);

      const trades: FlowTrade[] = rows
        .map((r) => normalizeFlowTradeRow(r, symbol))
        .filter((t): t is FlowTrade => !!t)
        .filter((t) => t.timestamp > 0 && t.premium > 0 && t.strike > 0 && !!t.expiry);

      trades.sort((a, b) => a.timestamp - b.timestamp);
      setFlowTrades(trades);

      const { profile, skew, ladders, series } = deriveFlowViews(trades);
      setOptionsProfile(profile);
      setSkewPoints(skew);

      // State ladders from classified flow
      setStateLadders(ladders);

      // Orderflow series from timestamped trades
      setOrderflowSeries(series);

      const now = Date.now();
      const byStrike = new Map<number, { callNet: number; putNet: number }>();
      for (const p of profile) byStrike.set(p.strike, { callNet: p.callNet, putNet: p.putNet });
      historyRef.current.push({ ts: now, byStrike });
      historyRef.current = historyRef.current.filter((s) => now - s.ts <= 2 * 60 * 60 * 1000);

      const s1 = nearestProfileSnapshot(historyRef.current, now - 1 * 60 * 1000);
      const s5 = nearestProfileSnapshot(historyRef.current, now - 5 * 60 * 1000);
      const s15 = nearestProfileSnapshot(historyRef.current, now - 15 * 60 * 1000);
      const s30 = nearestProfileSnapshot(historyRef.current, now - 30 * 60 * 1000);
      const lb: OptionsProfileLookbackByStrike = {};
      for (const p of profile) {
        const k = String(p.strike);
        const p1 = s1?.byStrike.get(p.strike);
        const p5 = s5?.byStrike.get(p.strike);
        const p15 = s15?.byStrike.get(p.strike);
        const p30 = s30?.byStrike.get(p.strike);
        lb[k] = {
          call1m: p1?.callNet ?? p.callNet,
          call5m: p5?.callNet ?? p.callNet,
          call15m: p15?.callNet ?? p.callNet,
          call30m: p30?.callNet ?? p.callNet,
          put1m: p1?.putNet ?? p.putNet,
          put5m: p5?.putNet ?? p.putNet,
          put15m: p15?.putNet ?? p.putNet,
          put30m: p30?.putNet ?? p.putNet,
        };
      }
      setProfileLookbackByStrike(lb);
    } catch {
      // silent
    } finally {
      inFlightKeysRef.current.delete(key);
      setLoading(false);
    }
  }, [symbol, profileMode, fetchOptions?.historyHours, fetchOptions?.limit, fetchOptions?.minPremium, fetchOptions?.symbolScoped]);

  useEffect(() => {
    fetch_();
    timerRef.current = setInterval(fetch_, REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [fetch_]);

  return {
    flowTrades,
    optionsProfile,
    profileLookbackByStrike,
    skewPoints,
    stateLadders,
    orderflowSeries,
    loading,
    flowMeta,
  };
}

export function deriveFlowViews(trades: FlowTrade[]): {
  profile: OptionsProfileStrike[];
  skew: SkewPoint[];
  ladders: StateLadders;
  series: OrderflowSeriesPoint[];
} {
  const agg = new Map<number, { callNet: number; putNet: number; callIvSum: number; callIvN: number; putIvSum: number; putIvN: number }>();
  for (const t of trades) {
    if (!Number.isFinite(t.strike) || t.strike <= 0) continue;
    const row = agg.get(t.strike) || { callNet: 0, putNet: 0, callIvSum: 0, callIvN: 0, putIvSum: 0, putIvN: 0 };
    const signedVol = t.position === 'LONG' ? t.volume : t.position === 'SHORT' ? -t.volume : 0;
    if (t.side === 'CALL') {
      row.callNet += signedVol;
      if ((t.iv || 0) > 0) {
        row.callIvSum += t.iv as number;
        row.callIvN += 1;
      }
    } else {
      row.putNet += signedVol;
      if ((t.iv || 0) > 0) {
        row.putIvSum += t.iv as number;
        row.putIvN += 1;
      }
    }
    agg.set(t.strike, row);
  }

  const profile: OptionsProfileStrike[] = [];
  const skew: SkewPoint[] = [];
  agg.forEach((v, strike) => {
    profile.push({
      strike,
      callNet: v.callNet,
      putNet: v.putNet,
      net: v.callNet + v.putNet,
    });
    skew.push({
      strike,
      callIv: v.callIvN > 0 ? v.callIvSum / v.callIvN : null,
      putIv: v.putIvN > 0 ? v.putIvSum / v.putIvN : null,
    });
  });
  profile.sort((a, b) => a.strike - b.strike);
  skew.sort((a, b) => a.strike - b.strike);

  const ladders = buildStateLadders(profile, trades);
  const series = buildOrderflowSeries(trades);
  return { profile, skew, ladders, series };
}

function classifyPositionFromRow(row: any): FlowTrade['position'] {
  return inferFlowTradePosition(row);
}

function pickNumber(row: any, keys: string[]): number {
  for (const k of keys) {
    const v = Number(row?.[k]);
    if (Number.isFinite(v) && v !== 0) return v;
  }
  return 0;
}

function normalizeFlowTradeRow(row: any, fallbackSymbol: string): FlowTrade | null {
  let ts = 0;
  const rawTs = row.timestamp || row.Timestamp || row.time || row.Time || row.date || row.Date;
  if (typeof rawTs === 'number') {
    ts = rawTs > 1e12 ? rawTs : rawTs * 1000;
  } else if (rawTs) {
    ts = new Date(rawTs).getTime();
  }

  const optRaw = String(row['Option Type'] || row.option_type || '').toUpperCase();
  const side: 'CALL' | 'PUT' =
    optRaw === 'C' || optRaw.startsWith('CALL') ? 'CALL' : 'PUT';
  const tradeSideRaw = String(
    row.trade_side ||
      row.Trade_Side ||
      row.Bid_Ask ||
      row.bid_ask ||
      row.Fill_vs_Spread ||
      row.fill_vs_spread ||
      ''
  ).trim();
  const position = classifyPositionFromRow(row);

  const greekDelta = pickNumber(row, ['delta', 'Delta', 'delta_greek', 'greek_delta']);
  const greekGamma = pickNumber(row, ['gamma', 'Gamma', 'gamma_greek', 'greek_gamma']);
  const spot = pickNumber(row, ['spot', 'Spot', 'Underlying Price', 'underlying_price', 'Stock']);
  const fallbackDeltaFromImbalance = pickNumber(row, ['Delta ( ask - bid )', 'delta_ask_bid', 'delta_accumelated', 'Delta accumelated']);
  const strike = Number(row.strike || row.Strike || 0);
  let expiry = String(row.expiry || row['Expiry Date'] || '').trim();
  const expDigits = expiry.replace(/\D/g, '');
  if (expDigits.length === 8 && !expiry.includes('-')) {
    expiry = `${expDigits.slice(0, 4)}-${expDigits.slice(4, 6)}-${expDigits.slice(6, 8)}`;
  }
  const volume = Number(row.volume || row.Volume || 0);
  const price = Number(row.price || row.Price || row.last || row.Last || row.spot || row.Spot || row['Underlying Price'] || row.strike || row.Strike || 0);
  const premiumRaw = Number(row.premium || row.Premium || row.notional || 0);
  const premium = premiumRaw > 0 ? premiumRaw : (volume > 0 && price > 0 ? volume * price * 100 : 0);

  const trade: FlowTrade = {
    timestamp: ts,
    symbol:
      row.Underlying ||
      row.underlying ||
      row.Ticker ||
      row.ticker ||
      row.symbol ||
      row.Symbol ||
      fallbackSymbol,
    strike,
    side,
    position,
    expiry,
    iv: Number(row.iv || row.IV || row['Implied Volatility'] || 0) || 0,
    delta: greekDelta || fallbackDeltaFromImbalance || 0,
    gamma: greekGamma || 0,
    spot: spot || 0,
    tradeSideRaw,
    premium,
    volume,
    price,
  };

  return hasCoreFlowFields(trade) ? trade : null;
}

function hasCoreFlowFields(t: FlowTrade): boolean {
  const expOk = t.expiry.replace(/\D/g, '').length >= 8;
  return (
    Number.isFinite(t.timestamp) &&
    t.timestamp > 0 &&
    Number.isFinite(t.strike) &&
    t.strike > 0 &&
    expOk &&
    Number.isFinite(t.volume) &&
    t.volume >= 0 &&
    Number.isFinite(t.premium) &&
    t.premium >= 0
  );
}

function applyFlowProfileMode(rows: any[], mode: GEXProfileMode): any[] {
  if (!rows.length) return rows;
  if (mode === 'full') return rows;
  const now = Date.now();
  const withExpiry = rows
    .map((r) => {
      const exp = String(r['Expiry Date'] ?? r.expiry ?? '').slice(0, 10);
      const ts = exp ? Date.parse(exp) : NaN;
      return { r, ts };
    })
    .filter((x) => Number.isFinite(x.ts) && x.ts >= now - 24 * 60 * 60 * 1000);
  if (!withExpiry.length) return rows;
  const uniq = Array.from(new Set(withExpiry.map((x) => x.ts))).sort((a, b) => a - b);
  const target = mode === 'latest' ? uniq[0] : (uniq[1] ?? uniq[0]);
  return withExpiry.filter((x) => x.ts === target).map((x) => x.r);
}

function nearestProfileSnapshot(history: ProfileSnapshot[], targetTs: number): ProfileSnapshot | null {
  if (!history.length) return null;
  let chosen: ProfileSnapshot | null = null;
  for (const s of history) {
    if (s.ts <= targetTs) chosen = s;
  }
  return chosen ?? history[0];
}

function buildStateLadders(profile: OptionsProfileStrike[], trades: FlowTrade[]): StateLadders {
  const dex: LadderBar[] = [];
  const convexity: LadderBar[] = [];
  const vanna: LadderBar[] = [];
  const charm: LadderBar[] = [];

  const now = Date.now();
  const byStrike = new Map<number, {
    longCount: number;
    shortCount: number;
    dex: number;
    convexity: number;
    ivW: number;
    ivN: number;
    nearestExpiryMs: number | null;
  }>();
  for (const t of trades) {
    if (!t.strike || t.strike <= 0) continue;
    const exTs = t.expiry ? Date.parse(String(t.expiry).slice(0, 10)) : NaN;
    const rec = byStrike.get(t.strike) || {
      longCount: 0,
      shortCount: 0,
      dex: 0,
      convexity: 0,
      ivW: 0,
      ivN: 0,
      nearestExpiryMs: Number.isFinite(exTs) ? exTs : null,
    };
    if (t.position === 'LONG') rec.longCount += t.volume;
    if (t.position === 'SHORT') rec.shortCount += t.volume;
    const deltaAbs = Math.abs(Number(t.delta || 0));
    const gammaAbs = Math.abs(Number(t.gamma || 0));
    const bullish = (t.position === 'LONG' && t.side === 'CALL') || (t.position === 'SHORT' && t.side === 'PUT');
    const bearish = (t.position === 'SHORT' && t.side === 'CALL') || (t.position === 'LONG' && t.side === 'PUT');
    const dexImpact = (bullish ? 1 : bearish ? -1 : 0) * Math.max(0.01, deltaAbs) * t.volume * 100;
    rec.dex += dexImpact;
    const convexityImpact = (t.position === 'LONG' ? 1 : t.position === 'SHORT' ? -1 : 0) * Math.max(0.0001, gammaAbs) * t.volume * 100;
    rec.convexity += convexityImpact;
    if ((t.iv || 0) > 0) {
      rec.ivW += (t.iv as number) * t.volume;
      rec.ivN += t.volume;
    }
    if (Number.isFinite(exTs)) {
      rec.nearestExpiryMs = rec.nearestExpiryMs == null ? exTs : Math.min(rec.nearestExpiryMs, exTs);
    }
    byStrike.set(t.strike, rec);
  }

  for (const p of profile) {
    const rec = byStrike.get(p.strike);
    const netDex = rec ? rec.dex : (p.callNet - p.putNet);
    const netConv = rec ? rec.convexity : (p.callNet + p.putNet);
    dex.push({ strike: p.strike, value: netDex });
    convexity.push({ strike: p.strike, value: netConv });

    // Real-data proxy from classified volume + IV + time-to-expiry
    const hoursToExp = rec?.nearestExpiryMs ? Math.max(1, (rec.nearestExpiryMs - now) / (1000 * 60 * 60)) : 24;
    const posBias = (rec?.shortCount || 0) - (rec?.longCount || 0);
    const ivAvg = rec && rec.ivN > 0 ? (rec.ivW / rec.ivN) : 0.2;
    const vannaVal = netConv * ivAvg * Math.sign(netDex || 1) * (24 / Math.max(24, hoursToExp));
    const charmVal = posBias * (1 / Math.max(1, hoursToExp));
    vanna.push({ strike: p.strike, value: vannaVal });
    charm.push({ strike: p.strike, value: charmVal });
  }

  const byStrikeAsc = (a: LadderBar, b: LadderBar) => a.strike - b.strike;
  dex.sort(byStrikeAsc);
  convexity.sort(byStrikeAsc);
  vanna.sort(byStrikeAsc);
  charm.sort(byStrikeAsc);
  return { dex, convexity, vanna, charm };
}

function buildOrderflowSeries(trades: FlowTrade[]): OrderflowSeriesPoint[] {
  if (!trades.length) return [];
  const byMinute = new Map<number, {
    spot: number;
    dex: number;
    gex: number;
    convexity: number;
    netGex: number;
    netConvexity: number;
  }>();

  const minute = (ts: number) => Math.floor(ts / 60000) * 60000;
  let runningNetGex = 0;
  let runningNetConv = 0;
  for (const t of trades) {
    const m = minute(t.timestamp);
    const rec = byMinute.get(m) || { spot: t.price || t.strike, dex: 0, gex: 0, convexity: 0, netGex: 0, netConvexity: 0 };
    const dir = t.position === 'LONG' ? 1 : t.position === 'SHORT' ? -1 : 0;
    const sideSign = t.side === 'CALL' ? 1 : -1;
    const bullish = (t.position === 'LONG' && t.side === 'CALL') || (t.position === 'SHORT' && t.side === 'PUT');
    const bearish = (t.position === 'SHORT' && t.side === 'CALL') || (t.position === 'LONG' && t.side === 'PUT');
    const deltaAbs = Math.abs(Number(t.delta || 0));
    const gammaAbs = Math.abs(Number(t.gamma || 0));

    // dex orderflow = bullish volume*delta - bearish volume*delta (share equivalent)
    const dexImpact = (bullish ? 1 : bearish ? -1 : 0) * Math.max(0.01, deltaAbs) * t.volume * 100;

    // gex orderflow = call gex imbalance - put gex imbalance
    const gexImpact = dir * sideSign * Math.max(0.0001, gammaAbs) * t.volume * 100;

    // convexity orderflow = long orderflow*gex - short orderflow*gex
    const convexityImpact = (dir === 0 ? 0 : (dir > 0 ? 1 : -1)) * Math.max(0.0001, gammaAbs) * t.volume * 100;

    rec.spot = t.price || rec.spot;
    rec.dex += dexImpact;
    rec.gex += gexImpact;
    rec.convexity += convexityImpact;
    runningNetGex += gexImpact;
    runningNetConv += convexityImpact;
    rec.netGex = runningNetGex;
    rec.netConvexity = runningNetConv;
    byMinute.set(m, rec);
  }

  return Array.from(byMinute.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([t, v]) => ({ t, ...v }));
}
